import { useEffect, type DependencyList, type ReactNode } from 'react';
import { create } from 'zustand';

/**
 * Lets the current page inject its header (a `title` + optional `extras`, e.g. the dashboard's
 * "Last import" stamp) into the global Topbar, which renders this slot. Transient — not persisted,
 * like `recalcStore`.
 *
 * Only the dashboard fills it today (its in-page `PageHeader` moved into the top bar per the Figma).
 * Pages that don't fill it keep their own in-page `PageHeader`, and the Topbar shows just the global
 * controls — so there's never a double title.
 */
interface PageHeaderState {
  title: ReactNode | null;
  /** Rendered UNDER the title (2026-07-27). `extras` is right-aligned, so a page subtitle needs its
   *  own slot — putting it in `extras` would park the description beside the global controls. */
  subtitle: ReactNode | null;
  extras: ReactNode | null;
  set: (content: { title?: ReactNode; subtitle?: ReactNode; extras?: ReactNode }) => void;
  clear: () => void;
}

export const usePageHeaderStore = create<PageHeaderState>(set => ({
  title: null,
  subtitle: null,
  extras: null,
  set: (content) => set({
    title: content.title ?? null,
    subtitle: content.subtitle ?? null,
    extras: content.extras ?? null,
  }),
  clear: () => set({ title: null, subtitle: null, extras: null }),
}));

/**
 * Register this page's Topbar header content. Re-sets when `deps` change (e.g. the dashboard's
 * last-import stamp once data loads) and clears on unmount only (separate effect) so navigating
 * away empties the slot without blanking it between dep updates.
 */
export function usePageHeader(
  content: { title?: ReactNode; subtitle?: ReactNode; extras?: ReactNode },
  deps: DependencyList = [],
): void {
  const set = usePageHeaderStore(s => s.set);
  const clear = usePageHeaderStore(s => s.clear);
  // Update on mount + whenever the caller's deps change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { set(content); }, deps);
  // Clear only on real unmount.
  useEffect(() => () => clear(), [clear]);
}
